import React, { useCallback, useEffect, useState } from 'react';

import fileDownload from 'react-file-download';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';

import { Container } from '@mui/material';

import { property as propertyGlobals } from '@luxuryescapes/lib-global';

import PageHeader from '~/components/Common/Elements/PageHeader';

import BedbankService from '../../../services/BedbankService';
import OffersService from '../../../services/OffersService';
import { getTaxesAndFeesPDF } from '../../../services/PDFService';
import ReservationService from '../../../services/ReservationService';
import VendorsService from '../../../services/VendorsService';
import { reportError } from '../../../utils/reportError';
import Spinner from '../../Common/Spinner';

import PropertyPage from './PropertyPage';
import RentalPropertyForm from './RentalPropertyForm';
import cleanProperty from './helpers/cleanProperty';

function PropertyPageContainer() {
  const { id_vendor: vendorId, id_property: propertyId } = useParams();
  const history = useHistory();

  const [property, setProperty] = useState({});
  const [vendor, setVendor] = useState({});
  const [ratePlans, setRatePlans] = useState([]);
  const [vendorIdState, setVendorIdState] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offers, setOffers] = useState([]);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const fetchData = useCallback(
    async (propertyId, vendorId) => {
      const property = await ReservationService.getProperty(propertyId);
      if (vendorId === 'unknown') {
        const newVendorId = property.result.id_salesforce_external;
        history.replace(`/vendors/${newVendorId}/properties/${propertyId}`);
        return fetchData(propertyId, newVendorId);
      }
      const [vendor, ratePlans, offers] = await Promise.all([
        VendorsService.getVendorById(vendorId),
        ReservationService.getRatePlans(vendorId),
        OffersService.getOffers({
          queryString: vendorId,
          limit: 250,
        }),
      ]);

      if (property.result.parent_property_id) {
        const parent = await ReservationService.getProperty(property.result.parent_property_id);
        if (parent && parent.result) {
          property.result.parentProperty = parent.result;
        }
      }

      if (property.result.bedbank_property_id) {
        const bedbank = await BedbankService.getPropertyById(property.result.bedbank_property_id);
        if (bedbank && bedbank.result) {
          property.result.bedbankProperty = bedbank.result;
        }
      }

      return {
        property: property.result,
        vendor: vendor.result,
        ratePlans: ratePlans.result,
        offers: offers.result,
      };
    },
    [history],
  );

  const downloadTaxesAndFees = useCallback(async () => {
    setDownloadInProgress(true);
    setDownloadFailed(false);

    try {
      const propertyName = property.name || property.id;
      const { taxes_and_fees, commissionable_taxes_and_fees, taxes_and_fees_content } = property;
      const pdf = await getTaxesAndFeesPDF({
        taxes: {
          taxes_and_fees,
          commissionable_taxes_and_fees,
          taxes_and_fees_content,
        },
        propertyName,
      });
      const fileName = `LuxuryEscapes-Taxes-And-Fees-${property.id}.pdf`;

      await fileDownload(pdf, fileName);
    } catch (error) {
      reportError(error);
      setDownloadFailed(true);
      setDownloadInProgress(false);
    } finally {
      setDownloadInProgress(false);
    }
  }, [property]);

  const handlePropertyUpdate = useCallback(
    async (changes) => {
      const propertyId = property.id;
      const updatedProperty = cleanProperty({ ...property, ...changes });

      setSaving(true);

      try {
        await ReservationService.updateProperty(updatedProperty, propertyId);
        const {
          property: updatedPropertyData,
          vendor: updatedVendor,
          ratePlans: updatedRatePlans,
          offers: updatedOffers,
        } = await fetchData(propertyId, vendorIdState);

        setSaving(false);
        setProperty(updatedPropertyData);
        setVendor(updatedVendor);
        setRatePlans(updatedRatePlans);
        setOffers(updatedOffers);
        setFetching(false);
      } catch (error) {
        reportError(error);
        setSaving(false);
      }
    },
    [property, vendorIdState, fetchData],
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const {
          property: propertyData,
          vendor: vendorData,
          ratePlans: ratePlansData,
          offers: offersData,
        } = await fetchData(propertyId, vendorId);

        setProperty(propertyData);
        setVendor(vendorData);
        setRatePlans(ratePlansData);
        setVendorIdState(vendorData.id);
        setOffers(offersData);
        setFetching(false);
      } catch (error) {
        reportError(error);
      }
    };

    loadData();
  }, [propertyId, vendorId, fetchData]);

  if (fetching) {
    return <Spinner />;
  }

  const pageTitle = `Vendors | ${vendor.name} | ${property.name}`;

  return (
    <Container maxWidth="xl" className="property-edit-form">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <PageHeader title={pageTitle} backButton={`/vendors/${vendorIdState}`} />

      {property.type !== propertyGlobals.RENTAL_TYPE && (
        <PropertyPage
          property={property}
          ratePlans={ratePlans}
          vendorId={vendorIdState}
          downloadInProgress={downloadInProgress}
          downloadFailed={downloadFailed}
          offers={offers}
          downloadTaxesAndFees={downloadTaxesAndFees}
        />
      )}

      {property.type === propertyGlobals.RENTAL_TYPE && (
        <RentalPropertyForm
          property={property}
          ratePlans={ratePlans}
          vendorId={vendorIdState}
          vendorCurrencyCode={vendor.currencyisocode}
          downloadInProgress={downloadInProgress}
          downloadFailed={downloadFailed}
          offers={offers}
          downloadTaxesAndFees={downloadTaxesAndFees}
          updateProperty={handlePropertyUpdate}
          isPropertyUpdating={saving}
        />
      )}
    </Container>
  );
}

export default PropertyPageContainer;
